/**
 * Registry of available slider strategies.
 * You can add custom strategies here if needed.
 */
const SliderStrategies = {
  default: class DefaultStrategy {
    constructor(slider) {
      this.slider = slider;
    }
    renderControls() {}
    updateButtonsState() {
      this.slider.updateButtonState();
    }
    updateDotsState() {
      if (!this.slider.dotsContainer) return;
      const dots = this.slider.dotsContainer.querySelectorAll('button');
      dots.forEach((dot, i) => {
        const selected = i === this.slider.currentIndex;
        dot.classList.toggle('tw-bg-marine', selected);
        dot.classList.toggle('tw-bg-gray-400', !selected);
        dot.setAttribute('aria-selected', selected ? 'true' : 'false');
        dot.tabIndex = selected ? 0 : -1;
      });
    }
    updateActiveCard() {
      this.slider.items.forEach((item, i) => {
        const isActive = i === this.slider.currentIndex;
        item.setAttribute('aria-selected', isActive ? 'true' : 'false');
        item.tabIndex = isActive ? 0 : -1;
      });
    }
  }
};

class CustomSlider {
  constructor(root) {
    if (!root) return;

    this.root = root;
    this.container = root.querySelector('[data-slider="container"]');
    this.items = Array.from(root.querySelectorAll('[data-slider="item"]'));
    this.prevBtn = root.querySelector('[data-slider="prev"]');
    this.nextBtn = root.querySelector('[data-slider="next"]');
    this.dotsContainer = root.querySelector('[data-slider="dots"]');

    if (!this.container || this.items.length === 0) return;

    this.currentIndex = 0;
    this.totalItems = this.items.length;
    this.scrollMode = root.dataset.scrollMode || 'card-wise'; // 'page-wise' or 'card-wise'
    this.orientation = root.dataset.orientation || 'horizontal'; // 'horizontal' or 'vertical'

    // Bind scroll methods according to scrollMode
    if (this.scrollMode === 'page-wise') {
      this.goNext = this.goNextPageWise.bind(this);
      this.goPrev = this.goPrevPageWise.bind(this);
    } else {
      this.goNext = this.goNextCardWise.bind(this);
      this.goPrev = this.goPrevCardWise.bind(this);
    }

    // Drag state
    this.dragging = false;
    this.dragStartPos = 0;
    this.dragCurrentPos = 0;
    this.startTranslate = 0;

    // Load strategy from data attribute, fallback to default
    const strategyName = root.dataset.strategy || 'default';
    const StrategyClass = SliderStrategies[strategyName] || SliderStrategies['default'];
    this.strategy = new StrategyClass(this);

    this.init();
  }

  init() {
    this.strategy.renderControls();
    this.attachEvents();
    this.setupDots();
    this.updateUI();

    window.addEventListener('resize', this.debounce(() => this.handleResize(), 150));
  }

  /** Get size of one card (width or height) */
  getCardSize() {
    const firstItem = this.items[0];
    if (!firstItem) return 0;
    return this.orientation === 'horizontal' ? firstItem.offsetWidth : firstItem.offsetHeight;
  }

  /** Get full step size including gap/margin by measuring distance between first and second cards */
  getStepSize() {
    if (this.items.length < 2) return this.getCardSize();

    const firstRect = this.items[0].getBoundingClientRect();
    const secondRect = this.items[1].getBoundingClientRect();

    return this.orientation === 'horizontal'
      ? secondRect.left - firstRect.left
      : secondRect.top - firstRect.top;
  }

  getVisibleCardsCount() {
    const containerSize = this.orientation === 'horizontal' ? this.container.offsetWidth : this.container.offsetHeight;
    return Math.floor(containerSize / this.getStepSize());
  }

  getCurrentTranslate() {
    const style = getComputedStyle(this.container);
    const matrix = style.transform || style.webkitTransform || style.mozTransform;
    if (!matrix || matrix === 'none') return 0;
    const values = matrix.match(/matrix.*\((.+)\)/)[1].split(', ');
    return this.orientation === 'horizontal' ? parseFloat(values[4]) : parseFloat(values[5]);
  }

  detectPartialVisibleIndex() {
    const translate = this.getCurrentTranslate();
    return Math.floor(-translate / this.getStepSize());
  }

  goNextCardWise() {
    if (this.currentIndex >= this.totalItems - 1) return;
    this.currentIndex++;
    this.updateUI();
  }

  goPrevCardWise() {
    if (this.currentIndex <= 0) return;
    this.currentIndex--;
    this.updateUI();
  }

  goNextPageWise() {
    const partialIndex = this.detectPartialVisibleIndex();
    const visibleCards = this.getVisibleCardsCount();
    const maxIndex = this.totalItems - 1;
    let nextIndex = partialIndex + visibleCards;
    if (nextIndex > maxIndex) nextIndex = maxIndex;
    this.currentIndex = nextIndex;
    this.updateUI();
  }

  goPrevPageWise() {
    const partialIndex = this.detectPartialVisibleIndex();
    const visibleCards = this.getVisibleCardsCount();
    let prevIndex = partialIndex - visibleCards;
    if (prevIndex < 0) prevIndex = 0;
    this.currentIndex = prevIndex;
    this.updateUI();
  }

  scrollToIndex(index) {
    index = Math.max(0, Math.min(index, this.totalItems - 1));
    this.currentIndex = index;
    const stepSize = this.getStepSize();
    const offset = -index * stepSize;
    if (this.orientation === 'horizontal') {
      this.container.style.transition = 'transform 0.3s ease-in-out';
      this.container.style.transform = `translateX(${offset}px)`;
    } else {
      this.container.style.transition = 'transform 0.3s ease-in-out';
      this.container.style.transform = `translateY(${offset}px)`;
    }
    this.strategy.updateButtonsState();
    this.strategy.updateDotsState();
    this.strategy.updateActiveCard();
  }

  updateButtonState() {
    if (!this.prevBtn || !this.nextBtn) return;

    const containerSize = this.orientation === 'horizontal' ? this.container.offsetWidth : this.container.offsetHeight;
    const stepSize = this.getStepSize();
    const totalCardsSize = stepSize * this.totalItems;

    if (totalCardsSize <= containerSize) {
      this.prevBtn.disabled = true;
      this.nextBtn.disabled = true;
      return;
    }

    this.prevBtn.disabled = this.currentIndex === 0;
    this.nextBtn.disabled = this.currentIndex >= this.totalItems - 1;
  }

  attachEvents() {
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.goPrev());
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.goNext());
    if (this.dotsContainer) {
      this.dotsContainer.addEventListener('click', e => {
        const btn = e.target.closest('button[data-index]');
        if (!btn) return;
        this.goToIndex(Number(btn.dataset.index));
      });
    }
    this.root.addEventListener('keydown', e => this.handleKeyDown(e));

    this.container.style.touchAction = this.orientation === 'horizontal' ? 'pan-y' : 'pan-x';

    this.container.addEventListener('pointerdown', e => this.onDragStart(e));
    this.container.addEventListener('pointermove', e => this.onDragMove(e));
    this.container.addEventListener('pointerup', e => this.onDragEnd(e));
    this.container.addEventListener('pointercancel', e => this.onDragEnd(e));
    this.container.addEventListener('pointerleave', e => this.onDragEnd(e));
  }

  handleKeyDown(e) {
    if (e.target.closest('[data-slider="item"]') !== e.target) return;
    const keysPrev = this.orientation === 'horizontal' ? 'ArrowLeft' : 'ArrowUp';
    const keysNext = this.orientation === 'horizontal' ? 'ArrowRight' : 'ArrowDown';
    if (e.key === keysPrev) {
      e.preventDefault();
      this.goPrev();
    } else if (e.key === keysNext) {
      e.preventDefault();
      this.goNext();
    }
  }

  onDragStart(e) {
    if (e.pointerType !== 'touch' && e.pointerType !== 'mouse') return;
    this.dragStartPos = this.orientation === 'horizontal' ? e.clientX : e.clientY;
    this.dragCurrentPos = this.dragStartPos;
    this.startTranslate = this.getCurrentTranslate();
    this.dragging = true;
    this.isDragging = false;
    this.container.style.transition = 'none';
  }

  onDragMove(e) {
    if (!this.dragging) return;
    const currentPos = this.orientation === 'horizontal' ? e.clientX : e.clientY;
    const delta = currentPos - this.dragStartPos;
    if(!this.isDragging) {
      if (Math.abs(delta) < 10) return;
      this.isDragging = true;
      this.container.setPointerCapture(e.pointerId);
    }
    this.dragging = true;
    let newTranslate = this.startTranslate + delta;
    const maxTranslate = 0;
    const minTranslate = -this.getStepSize() * (this.totalItems - 1);
    if (newTranslate > maxTranslate) newTranslate = maxTranslate;
    if (newTranslate < minTranslate) newTranslate = minTranslate;
    if (this.orientation === 'horizontal') {
      this.container.style.transform = `translateX(${newTranslate}px)`;
    } else {
      this.container.style.transform = `translateY(${newTranslate}px)`;
    }
  }

  onDragEnd(e) {
    if (!this.dragging) return;
    this.dragging = false;
    this.container.style.transition = 'transform 0.3s ease-in-out';

    if (!e) return;
    const endPos = this.orientation === 'horizontal' ? e.clientX : e.clientY;
    const dragDistance = endPos - this.dragStartPos;
    const threshold = this.getStepSize() / 4;

    let newIndex = this.currentIndex;
    if (dragDistance > threshold) {
      newIndex = Math.max(this.currentIndex - 1, 0);
    } else if (dragDistance < -threshold) {
      newIndex = Math.min(this.currentIndex + 1, this.totalItems - 1);
    }
    this.scrollToIndex(newIndex);

    if (e.pointerId) {
      this.container.releasePointerCapture(e.pointerId);
    }
  }

  handleResize() {
    const maxIndex = this.totalItems - 1;
    if (this.currentIndex > maxIndex) this.currentIndex = maxIndex;
    this.updateUI();
  }

  debounce(func, wait) {
    let timeout;
    return (...args) => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  updateUI() {
    this.scrollToIndex(this.currentIndex);
  }

  setupDots() {
    if (!this.dotsContainer) return;
    this.dotsContainer.innerHTML = '';
    for (let i = 0; i < this.totalItems; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.dataset.index = i;
      dot.className = 'tw-w-2 tw-h-2 tw-rounded-full tw-bg-gray-400';
      if (i === this.currentIndex) dot.classList.add('tw-bg-marine');
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-selected', i === this.currentIndex ? 'true' : 'false');
      dot.setAttribute('aria-controls', `tabpanel-${i}`);
      dot.id = `tab-${i}`;
      dot.addEventListener('click', () => this.goToIndex(i));
      this.dotsContainer.appendChild(dot);
    }
  }

  goToIndex(index) {
    index = Math.max(0, Math.min(index, this.totalItems - 1));
    this.currentIndex = index;
    this.updateUI();
  }
}

// Initialize sliders on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-component="custom-slider"]').forEach(root => {
    new CustomSlider(root);
  });
});